import React from 'react';
import { GroupedVirtuosoHandle, Virtuoso } from 'react-virtuoso';
import NoDataMessage from '../../../NoDataMessage';
import { LedgerTableSkeleton, LedgerRowSkeleton, LoadingContainer } from './LedgerTableSkeleton';
import { useLedgerList } from 'general-ledger/fast-gl/hooks/useLedgerList';
import DataEntryTable from 'general-ledger/fast-gl/components/data-entry-table/DataEntryTable';
import { LedgerCategoryHeader } from './LedgerCategoryHeader';
import { useGeneralLedger } from 'general-ledger/fast-gl/hooks/useGeneralLedger';
import { Icon } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { ErrorMessage } from '../../../../client-web-app/components';
import { LedgerTableFooter, EmptyElement } from './LedgerTable.styles';

export const LedgerTable = React.memo(
  React.forwardRef<GroupedVirtuosoHandle>((_, ref) => {
    const { ledgers, loading, error, qualityIssuesSummaryError, qualityIssuesSummaryLoading } =
      useLedgerList();
    const { hiddenCategories, updateHiddenCategories, search } = useGeneralLedger();

    const handleCategoryHeaderClick = (categoryId: string) => {
      if (!updateHiddenCategories) return;
      if (hiddenCategories.includes(categoryId)) {
        updateHiddenCategories(hiddenCategories.filter(id => id !== categoryId));
      } else {
        updateHiddenCategories([...hiddenCategories, categoryId]);
      }
    };

    if (loading) {
      return <LedgerTableSkeleton />;
    }

    if (error || qualityIssuesSummaryError) {
      return (
        <ErrorMessage message="There was an error loading ledgers. Please refresh the page." />
      );
    }

    // For an empty search, we may still have a quality issues summary but want to show an empty state
    const isEmptySearchResult =
      search && ledgers.length === 1 && ledgers[0].name === 'Quality Issues Summary';

    if (!loading && (!ledgers?.length || isEmptySearchResult)) {
      return <NoDataMessage />;
    }

    return (
      <>
        {qualityIssuesSummaryLoading ? (
          <LoadingContainer>
            <LedgerRowSkeleton tableRows={2} />
          </LoadingContainer>
        ) : null}
        <Virtuoso
          ref={ref}
          style={{
            height: 'calc(100vh - 64px)',
            padding: '0 32px',
            overflowX: 'hidden',
          }}
          data={ledgers}
          components={{
            // Footer exists purely to add a little bit of margin to the end of the page. Actual margins don't work because it messes with the
            // calculations for the virtualization.
            Footer: () => <LedgerTableFooter />,
          }}
          defaultItemHeight={800}
          itemContent={index => {
            const ledger = ledgers[index];
            const isFirstOfGroup =
              index === 0 || ledgers[index - 1].category.id !== ledger.category.id;
            const isQualityIssuesAndSearch =
              ledger.category.name === 'Quality Issues Summary' && search;
            const isHidden =
              hiddenCategories.includes(ledger.category.id) || isQualityIssuesAndSearch;
            return (
              <React.Fragment key={ledger.id}>
                {isFirstOfGroup && !isQualityIssuesAndSearch ? (
                  <LedgerCategoryHeader
                    onClick={() => {
                      handleCategoryHeaderClick(ledger.category.id);
                    }}
                  >
                    {ledger.category.name}
                    <Icon
                      icon={isHidden ? 'arrowDown' : 'arrowUp'}
                      color={colors.charcoal}
                      hoverColor={colors.smoke}
                      size="small"
                      viewBox="0 0 12 6"
                    />
                  </LedgerCategoryHeader>
                ) : (
                  // Virtuoso doesn't allow zero height. If we've hidden the header and the table, we need this 1px
                  // element to prevent a completely empty screen
                  <EmptyElement />
                )}
                {isHidden ? <EmptyElement /> : <DataEntryTable ledgerAccount={ledger} />}
              </React.Fragment>
            );
          }}
        />
      </>
    );
  }),
);

LedgerTable.displayName = 'LedgerTable';
